import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { seriesColor, useTheme } from '../../theme/readTheme';
import type { ChartData, ChartFields } from '../../types';
import { EChart } from '../EChart';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { buildFrame, foldToOther, frameNote, isEmpty } from '../frame';
import { cell } from '../format';
import { animation, AREA_OPACITY, legendFor, lineStyle, tooltipChrome } from '../options';

/**
 * Several measures compared across a few named axes — one closed polygon per
 * series over a shared set of spokes.
 *
 * Radar is for a handful of axes (3–8) and a handful of series: it shows a
 * shape, a profile, not precise magnitudes. The categories become the spokes,
 * each series a polygon. Each spoke is scaled to its own max so a measure that
 * happens to be large doesn't flatten the rest — the shape is the read, not
 * cross-axis comparison.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

const MAX_SERIES = 6;
const MAX_AXES = 10;

const radarView = (data: ChartData, theme: ReturnType<typeof useTheme>, fields?: ChartFields) => {
  const frame = foldToOther(buildFrame(data, fields), MAX_SERIES);
  if (isEmpty(frame)) return null;
  if (frame.categories.length < 3 || frame.categories.length > MAX_AXES) return null;

  // Per-axis max across every series, so each spoke uses its own scale.
  const axisMax = frame.categories.map((_, i) =>
    Math.max(...frame.series.map((s) => s.values[i] ?? 0), 0),
  );

  const indicator = frame.categories.map((name, i) => ({
    name,
    max: axisMax[i] || 1,
  }));

  const option: EChartsOption = {
    ...animation,
    legend: legendFor(theme, frame.series.length),
    // Built as DOM, never an HTML string: the series and axis names come from
    // the upstream API, so `textContent` is what keeps an app called
    // `<img onerror=…>` from executing in the tooltip.
    tooltip: {
      trigger: 'item',
      ...tooltipChrome(theme),
      formatter: (params: unknown) => {
        const p = params as { name?: string; value?: unknown };
        const values = Array.isArray(p.value) ? (p.value as number[]) : [];
        const root = document.createElement('div');
        root.className = 'chart-tip';
        const head = document.createElement('div');
        head.className = 'chart-tip__title';
        head.textContent = p.name ?? '';
        root.append(head);
        frame.categories.forEach((axis, k) => {
          const row = document.createElement('div');
          row.className = 'chart-tip__row';
          const n = document.createElement('span');
          n.className = 'chart-tip__name';
          n.textContent = axis;
          const v = document.createElement('strong');
          v.className = 'chart-tip__value';
          v.textContent = cell(values[k], 'exact');
          row.append(n, v);
          root.append(row);
        });
        return root;
      },
    },
    radar: {
      indicator,
      shape: 'polygon' as const,
      radius: '66%',
      center: ['50%', '52%'],
      axisName: { color: theme.inkSecondary, fontSize: 11, fontFamily: theme.font },
      splitLine: { lineStyle: { color: theme.grid } },
      splitArea: { areaStyle: { color: [theme.surface, theme.surfaceSunken] } },
      axisLine: { lineStyle: { color: theme.grid } },
    },
    series: [
      {
        type: 'radar' as const,
        data: frame.series.map((s, i) => {
          const color = seriesColor(theme, i);
          return {
            name: s.name,
            value: s.values.map((v) => v ?? 0),
            lineStyle: lineStyle(color),
            itemStyle: { color },
            areaStyle: { color, opacity: AREA_OPACITY },
          };
        }),
        emphasis: { lineStyle: { width: 3 } },
      },
    ],
  };

  return { option, note: frameNote(frame) };
};

export const RadarChart = ({ data, fields }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => radarView(data, theme, fields), [data, theme, fields]);
  if (!view) return <Empty message="Needs 3–10 named axes to plot" />;
  return (
    <Plot note={view.note}>
      <EChart option={view.option} ariaLabel="Radar chart" />
    </Plot>
  );
};
